import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rawApi, inversionApi } from '../../lib/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useSelection } from '../../contexts/SelectionContext';
import QCChartsModal from '../QCChartsModal';
import PseudosectionPane from './PseudosectionPane';
import SectionView from '../SectionView';
import useTerrainSettings from '../../hooks/useTerrainSettings';
import {
  PROPERTY_OPTIONS, hasTopography, panesFromResponse, levelRows, paneLayout, describeReading, indexOfSeq,
  readingSelection, isReadingSelectionFor, stackCanvases, fmtValue,
} from './pseudosection-helpers';

const errText = (e) => String(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed');

/**
 * PseudosectionPanel — section-style view of raw measured data.
 *
 *   datasetId  raw dataset: shows the measured pseudosection (property rhoa | ip | v_over_i)
 *   modelId    stored inversion model: measured / calculated / misfit panes (GET /datafit)
 *   model      client-side model (productToModel): the same via POST /api/inversion/datafit;
 *              `datasetId` then supplies positions and QC tiers when its quadrupoles match
 *   variant    'dock' (bottom panel on the map) | 'modal' (dataset page)
 *   onOpenViewer3d(datasetId)  the map's 3D (docked) action: a 3D survey has no pseudosection (`is_3d` in the
 *              response), the panel points to the 3D view's apparent-resistivity scatter instead
 *
 * Symbols are coloured by value on an x vs pseudo-depth canvas (QC tier D hollow); hover shows
 * the reading and its a/b/m/n, a click highlights the same quadrupole in every pane and is
 * published through SelectionContext as { type: 'reading', source: 'pseudosection',
 * datasetId, readingIndex } so the map's pseudosection layer can follow.
 */
export default function PseudosectionPanel({ datasetId = null, modelId = null, model = null, name = '', variant = 'dock', onClose, inversionModels = [], onOpenViewer3d = null }) {
  const { C } = useTheme();
  const { selection, setSelection, clearSelection } = useSelection();
  const datafit = !!(modelId || model);
  const [property, setProperty] = useState('rhoa');
  const [logOn, setLogOn] = useState(true);
  const terrain = useTerrainSettings();
  const [yChoice, setYChoice] = useState(null); // 'nlevel' only; depth/elevation follow the shared vertical reference
  const [ve, setVe] = useState(1);
  const [hollowTiers, setHollowTiers] = useState(true);
  const [resp, setResp] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(-1);
  const [hover, setHover] = useState(-1);
  const [showHist, setShowHist] = useState(false);
  const [view, setView] = useState('measured');
  // Inverted sections of this dataset: models linked to it, or every loaded model when none carry a link
  const linked = useMemo(() => {
    if (datafit || !datasetId) return [];
    const link = (m) => m.datasetId || m.dataset_id || null;
    const own = inversionModels.filter((m) => link(m) === datasetId);
    return own.length ? own : inversionModels.filter((m) => !link(m));
  }, [datafit, datasetId, inversionModels]);
  const inverted = view === 'inverted' && linked.length > 0;
  const canvases = useRef({});
  const registerCanvas = useCallback((id, el) => { if (el) canvases.current[id] = el; else delete canvases.current[id]; }, []);

  const propMeta = PROPERTY_OPTIONS.find((p) => p.id === property) || PROPERTY_OPTIONS[0];
  // Measured data: the log toggle follows the property until the user changes it
  useEffect(() => { setLogOn(datafit ? true : propMeta.log); }, [propMeta, datafit]);

  useEffect(() => {
    let live = true;
    setStatus('loading'); setError(''); setPicked(-1);
    let req;
    if (modelId) req = inversionApi.datafit(modelId, { qc: datasetId ? 1 : 0 });
    else if (model) {
      req = inversionApi.datafitFor({
        data_fit: model.dataFit,
        electrodes: (model.electrodes || []).map(({ id, x, y, z }) => ({ id, x, y: y || 0, z: z || 0 })),
        rms_percent: model.rmsPercent ?? null, dataset_id: datasetId || null, qc: !!datasetId,
      });
    } else req = rawApi.pseudosectionGrid(datasetId, { property, qc: 1, grid: 0 });
    req.then((res) => { if (live) { setResp(res.data); setStatus('ready'); } })
      .catch((e) => { if (live) { setResp(null); setError(errText(e)); setStatus('error'); } });
    return () => { live = false; };
  }, [datasetId, modelId, model, property]);

  const kind = datafit ? 'datafit' : 'measured';
  const panes = useMemo(
    () => panesFromResponse(resp, kind, { propertyMeta: propMeta, logOn }),
    [resp, kind, propMeta, logOn],
  );
  const points = resp?.points;
  const rows = useMemo(() => (points ? levelRows(points) : null), [points]);
  const elevAvailable = hasTopography(resp?.electrodes);
  const yMode = yChoice || (terrain.verticalRef === 'elevation' && elevAvailable ? 'elevation' : 'depth');
  const setYMode = (v) => {
    if (v === 'nlevel') { setYChoice('nlevel'); return; }
    setYChoice(null);
    terrain.set({ verticalRef: v });
  };
  const layout = useMemo(() => (points ? paneLayout({
    xRange: resp.x_range, depthRange: resp.depth_range, yMode, ve, nRows: rows?.rows.length || 1,
    electrodes: resp.electrodes, points,
  }) : null), [points, resp, yMode, ve, rows]);

  const mapDatasetId = resp?.dataset_id || datasetId;
  const canLink = !!mapDatasetId && (!datafit || resp?.source === 'dataset');
  // A reading picked on the map (or elsewhere) lights up here
  const external = useMemo(() => (
    canLink && points && isReadingSelectionFor(selection, mapDatasetId) && selection.source !== 'pseudosection'
      ? indexOfSeq(points, selection.readingIndex) : -1
  ), [canLink, points, selection, mapDatasetId]);
  const highlight = useMemo(() => [picked, external].filter((i) => i >= 0), [picked, external]);

  const onPick = (i) => {
    setPicked(i);
    if (!canLink) return;
    if (i >= 0) setSelection(readingSelection(mapDatasetId, points.seq[i]));
    else if (selection?.source === 'pseudosection') clearSelection();
  };

  const histModel = useMemo(() => (datafit && points ? {
    name: name || 'Data fit', rmsPercent: resp.rms_percent,
    iterations: model?.iterations || [], cells: points.misfit.map((v) => ({ misfit: v })),
    dataFit: { rhoa_meas: points.measured, rhoa_calc: points.calculated, seq: points.seq, iteration: model?.dataFit?.iteration ?? null },
  } : null), [datafit, points, resp, model, name]);

  const exportPng = () => {
    const list = panes.map((p) => canvases.current[p.id]).filter(Boolean);
    if (!list.length) return;
    const canvas = stackCanvases(list);
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = datafit ? 'datafit-pseudosections.png' : 'pseudosection.png';
    link.click();
  };

  const info = hover >= 0 ? hover : picked;
  const btn = { fontSize: 10, padding: '4px 9px', borderRadius: 6, cursor: 'pointer', background: C.bg4, border: `1px solid ${C.border}`, color: C.text1 };
  const label = { fontSize: 10, color: C.text2, display: 'flex', alignItems: 'center', gap: 4 };
  const sel = { fontSize: 11, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, borderRadius: 6, padding: '2px 6px' };
  const shell = variant === 'modal'
    ? { position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : null;
  const box = variant === 'modal'
    ? { width: 'min(1000px, 96vw)', maxHeight: '92vh', background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, display: 'flex', flexDirection: 'column' }
    : { height: 430, background: C.bg2, borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 };

  const body = (
    <div style={box} data-testid="pseudosection-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>
          {datafit ? `Data fit${name ? ` — ${name}` : ''}` : 'Pseudosection'}
        </span>
        {linked.length > 0 && (
          <span role="group" aria-label="Pseudosection view" style={{ display: 'inline-flex' }}>
            {[['measured', 'Measured'], ['inverted', 'Inverted']].map(([id, text]) => (
              <button key={id} type="button" aria-pressed={view === id} onClick={() => setView(id)}
                data-testid={'pseudosection-view-' + id}
                style={{ ...btn, borderRadius: id === 'measured' ? '6px 0 0 6px' : '0 6px 6px 0', background: view === id ? (C.bg1 || C.bg2) : C.bg4, fontWeight: view === id ? 600 : 400 }}>
                {text}
              </button>
            ))}
          </span>
        )}
        {resp && datafit && resp.rms_percent != null && (
          <span style={{ fontSize: 10, color: C.text2 }} data-testid="pseudosection-rms">
            RMS {fmtValue(resp.rms_percent)}% · {resp.total} readings{resp.iteration != null ? ` · iteration ${resp.iteration}` : ''}
          </span>
        )}
        {resp && !datafit && !inverted && (
          <span style={{ fontSize: 10, color: C.text2 }}>{resp.total} readings{resp.array_type ? ` · ${resp.array_type}` : ''}</span>
        )}
        <span style={{ flex: 1 }} />
        {/* The measured-plot controls do not apply to the inverted section (SectionView has its own). */}
        {!inverted && (
          <>
            {!datafit && (
              <label style={label}>Value{' '}
                <select value={property} onChange={(e) => setProperty(e.target.value)} style={sel} data-testid="pseudosection-property">
                  {PROPERTY_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </label>
            )}
            <label style={label}>
              <input type="checkbox" checked={logOn} onChange={(e) => setLogOn(e.target.checked)} data-testid="pseudosection-log" /> log scale
            </label>
            <label style={label}>View{' '}
              <select value={yMode} onChange={(e) => setYMode(e.target.value)} style={sel} data-testid="pseudosection-ymode">
                <option value="depth">pseudo-depth</option>
                <option value="elevation" disabled={!elevAvailable}>elevation (m)</option>
                <option value="nlevel">n-level</option>
              </select>
            </label>
            {yMode !== 'nlevel' && (
              <label style={label}>VE {ve}x{' '}
                <input type="range" min={0.5} max={6} step={0.5} value={ve} onChange={(e) => setVe(parseFloat(e.target.value))} data-testid="pseudosection-ve" />
              </label>
            )}
            {points?.tier && (
              <label style={label}>
                <input type="checkbox" checked={hollowTiers} onChange={(e) => setHollowTiers(e.target.checked)} /> hollow = QC tier D
              </label>
            )}
            {datafit && histModel && (
              <button style={btn} onClick={() => setShowHist(true)} data-testid="pseudosection-histogram">Misfit histogram</button>
            )}
            <button style={btn} onClick={exportPng} disabled={!panes.length} data-testid="pseudosection-png">PNG</button>
          </>
        )}
        <button onClick={onClose} aria-label="Close pseudosection" style={{ background: 'none', border: 'none', color: C.text2, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      {inverted && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} data-testid="pseudosection-inverted">
          <SectionView key={linked.map((m) => m.id).join('|')} inversionModels={linked}
            initialModelId={linked[linked.length - 1].id} onClose={() => setView('measured')} />
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 14px', display: inverted ? 'none' : undefined }}>
        {status === 'loading' && <div style={{ fontSize: 11, color: C.text2, padding: 20 }}>Loading pseudosection…</div>}
        {status === 'ready' && resp?.is_3d && !datafit && (
          <div data-testid="pseudosection-3d-note" style={{ fontSize: 11, color: C.text1, padding: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>3D survey: open the 3D view for the apparent-resistivity scatter</span>
            {onOpenViewer3d && datasetId && (
              <button style={btn} onClick={() => onOpenViewer3d(datasetId)} data-testid="pseudosection-open-3d">3D (docked)</button>
            )}
          </div>
        )}
        {status === 'error' && <div role="alert" style={{ fontSize: 11, color: C.red, padding: 20 }}>{error}</div>}
        {status === 'ready' && layout && panes.map((pane) => (
          <PseudosectionPane
            key={pane.id} pane={pane} points={points} layout={layout} rows={rows} electrodes={resp.electrodes}
            hollowTiers={hollowTiers} highlight={highlight} hover={hover} onHover={setHover} onPick={onPick}
            registerCanvas={registerCanvas} bg={C.bg1 || '#11161d'} fg={C.text2 || '#8b949e'}
          />
        ))}
      </div>

      {!inverted && (
        <div style={{ padding: '5px 14px', borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.text2, minHeight: 22, fontFamily: 'monospace' }} data-testid="pseudosection-info">
          {info >= 0 ? describeReading(resp, panes, info)
            : resp?.note || (resp?.truncated ? `Showing the first ${resp.total} readings (cap ${resp.max_readings || 50000}).` : 'Hover a reading; click to highlight the same quadrupole in every pane.')}
          {resp?.truncated && info >= 0 ? '  (truncated)' : ''}
          {resp && datafit ? `  ·  positions: ${resp.source === 'dataset' ? `raw dataset (${resp.join})` : 'model data block'}` : ''}
        </div>
      )}

      {showHist && histModel && (
        <QCChartsModal model={histModel} name={histModel.name} onClose={() => setShowHist(false)} />
      )}
    </div>
  );
  return shell ? <div style={shell} role="dialog" aria-modal="true">{body}</div> : body;
}
